import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AuthContext } from "shared/context/auth-context";
import { getTodayInTimezone } from "shared/utility/date";
import { isRideSkipUiEnabled, RIDE_SKIP_SERVICE_KINDS } from "./constants";
import { useRideSkipApi } from "./useRideSkipApi";
import RideSkipCountCell from "./RideSkipCountCell";
import "./RideSkipRosterPanel.css";

const studentIdOf = (student) => String(student?.id || student?._id || student || "");

const RideSkipRosterPanel = ({ serviceKind, serviceId, students, timezone }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const enabled = isRideSkipUiEnabled(auth?.company);
  const { listForService, upsert, isLoading } = useRideSkipApi();
  const today = getTodayInTimezone(timezone || auth?.company?.timezone || auth?.timezone, "yyyy-MM-dd");
  const [day, setDay] = useState(today);
  const [skips, setSkips] = useState([]);

  const roster = useMemo(() => (Array.isArray(students) ? students.filter(Boolean) : []), [students]);

  const load = async (nextDay = day) => {
    if (!serviceId || !serviceKind) return;
    const data = await listForService({ serviceKind, serviceId, day: nextDay });
    setSkips(Array.isArray(data?.skips) ? data.skips : []);
  };

  useEffect(() => {
    if (!enabled || !serviceId) return undefined;
    load(day);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, serviceId, serviceKind, day]);

  if (!enabled || !serviceId) return null;

  const skipByStudent = new Map(skips.map((row) => [String(row.studentId), row]));
  const count = skips.length;
  const isToday = day === today;

  const onToggle = async (student, nextActive) => {
    try {
      await upsert({
        studentId: studentIdOf(student),
        serviceKind,
        serviceId,
        day,
        active: nextActive,
      });
      await load(day);
    } catch (err) {
      /* toasted in api hook */
    }
  };

  return (
    <div className="ride-skip-panel">
      <div className="ride-skip-toolbar">
        <label className="ride-skip-day-label">
          {t("rideSkip.day")}
          <input
            className="ride-skip-day-input"
            type="date"
            min={today}
            value={day}
            onChange={(event) => setDay(event.target.value)}
          />
        </label>
        {isToday ? <span className="ride-skip-today-chip">{t("rideSkip.today")}</span> : null}
        {count > 0 ? <RideSkipCountCell count={count} /> : <span className="ride-skip-none">{t("rideSkip.noneMarked")}</span>}
      </div>
      <p className="ride-skip-hint">{t("rideSkip.hint")}</p>
      {roster.length === 0 ? (
        <p className="ride-skip-empty">{t("rideSkip.emptyRoster")}</p>
      ) : (
        <div className="ride-skip-grid">
          {roster.map((student) => {
            const sid = studentIdOf(student);
            const skip = skipByStudent.get(sid);
            const active = !!skip?.active;
            const name = student?.name || student?.fullname || "-";
            return (
              <label
                key={sid}
                className={`ride-skip-cell${active ? " is-skipped" : ""}${isLoading ? " is-disabled" : ""}`}
                title={name}
              >
                <input
                  type="checkbox"
                  checked={active}
                  disabled={isLoading}
                  onChange={(event) => onToggle(student, event.target.checked)}
                />
                <span className="ride-skip-cell-body">
                  <span className="ride-skip-cell-name">{name}</span>
                  {active ? <span className="ride-skip-cell-badge">{t("rideSkip.willSkip")}</span> : null}
                  {active && skip?.setByName ? (
                    <span className="ride-skip-cell-meta">{t("rideSkip.setBy", { name: skip.setByName })}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RideSkipRosterPanel;
export { RIDE_SKIP_SERVICE_KINDS };
